import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveAs } from 'file-saver';

import { ENetworkRequestStatus } from 'services/BackendApi';
import { formatDateTimeDisplayWithoutOffset } from 'utils';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import * as TaskManagementActions from 'store/modules/taskManagement/actions';
import { ITaskAttachment } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { renderFullName } from 'utils/guestInformation';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { CircleIconButton } from 'ui/CircleIconButton';
import TrashIcon from 'ui/Icons/tabler/trash.component.svg';
import DownloadIcon from 'ui/Icons/tabler/download.component.svg';
import { Table } from 'ui/Table';
import { ESortOrder } from 'store/common/types';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { deleteAttachmentRequestAction, setSelectedAttachmentIdAction } from 'store/modules/taskManagement/actions';
import { deleteAttachmentLoadingSelector, selectedAttachmentIdSelector } from 'store/modules/taskManagement/selectors';

const noop = () => {};

export interface ITaskAttachmentsTableProps {
  taskId: string;
  taskAttachments: ITaskAttachment[];
  taskAttachmentsLoading: ENetworkRequestStatus;
}


export const TaskAttachmentsTable: React.FC<ITaskAttachmentsTableProps> = ({ taskId, taskAttachments, taskAttachmentsLoading }) => {
  const dispatch = useDispatch();
  const deleteAttachmentLoading = useSelector(deleteAttachmentLoadingSelector);
  const selectedAttachmentId = useSelector(selectedAttachmentIdSelector);

  useEffect(() => {
    dispatch(TaskManagementActions.listAttachmentRequestAction(taskId));
  }, [taskId, dispatch]);

  const handleDeleteAttachment = useCallback(
    (taskId: string) => {
      dispatch(setSelectedAttachmentIdAction(taskId));
    },
    [selectedAttachmentId]
  );

  const handleConfirmDeleteAttachment = useCallback(
    () => {
      if (selectedAttachmentId) {
        dispatch(deleteAttachmentRequestAction(taskId));
      }
    },
    [selectedAttachmentId]
  );

  const handleCancelDeleteAttachment = useCallback(
    () => {
      dispatch(setSelectedAttachmentIdAction(null));
    },
    [selectedAttachmentId]
  );


  const columns: ITableColumn[] = useMemo(() => {
    return [
      { id: 'uploaded', label: 'Uploaded', width: '22px' },
      { id: 'documentTitle', label: 'Document title', width: '30px', className: 'text-ellipsis overflow-hidden' },
      { id: 'createdBy', label: 'Created By', width: '14px' },
      { id: 'actions', label: '', width: '12px' }
    ];
  }, []);

  const rows: ITableRow[] = useMemo(() => {
    return taskAttachments.map(item => {
      const createdByName = renderFullName(undefined, item.createdBy.firstName, item.createdBy.lastName);
      const actions = (
        <div className="item-actions flex justify-end items-center relative">
          <div className="w-4px h-34px rounded bg-brown-15 mr-10px" />
          <CircleIconButton
            type="secondary"
            className="download-task-attachment-button mx-1"
            IconComponent={DownloadIcon}
            iconSize="default"
            style={{ minWidth: '30px', minHeight: '30px' }}
            onClick={() => saveAs(item.url, item.name)}
          />
          <CircleIconButton
            type="secondary"
            className="delete-action mx-1"
            IconComponent={TrashIcon}
            iconSize="default"
            style={{ minWidth: '30px', minHeight: '30px' }}
            onClick={() => handleDeleteAttachment(item.id)}
          />
        </div>
      );
      return {
        id: item.id,
        cells: [
          { id: 'uploaded', value: formatDateTimeDisplayWithoutOffset(item.createdAt), title: formatDateTimeDisplayWithoutOffset(item.createdAt) },
          { id: 'documentTitle', value: item.name, title: item.name, className: 'text-ellipsis overflow-hidden whitespace-nowrap' },
          { id: 'createdBy', value: createdByName, title: createdByName },
          {
            id: 'actions',
            value: actions,
          },
        ]
      }
    })
  }, [handleDeleteAttachment, taskAttachments]);

  return (
    <div className="task-attachments w-full font-pt-sans">
      {selectedAttachmentId && (
          <ConfirmationModal
            type={EConfirmationModalType.WARNING}
            title='Delete Document.'
            message='Are you sure you want to delete this document?'
            confirmButtonLabel="Yes"
            cancelButtonLabel="No"
            onConfirm={handleConfirmDeleteAttachment}
            onCancel={handleCancelDeleteAttachment}
            isConfirmLoading={deleteAttachmentLoading === ENetworkRequestStatus.PENDING}
          />
        )}
      {taskAttachmentsLoading === ENetworkRequestStatus.PENDING && (
        <div className="mt-20px">
          <LoadingBar />
        </div>
      )}

      {taskAttachmentsLoading === ENetworkRequestStatus.ERROR && (
        <div className="mt-20px">
          <ErrorBar />
        </div>
      )}

      {taskAttachmentsLoading === ENetworkRequestStatus.SUCCESS && taskAttachments?.length > 0 && (
        <div className="max-h-[220px] overflow-auto border border-solid border-gray-40">
          <Table
            columns={columns}
            rows={rows}
            sortBy="createdAt"
            sortOrder={ESortOrder.DESC}
            evenColor="white-true"
            stickyHeader
            onSort={noop}
            messageWhenEmpty="No results"
          />
        </div>
      )}
    </div>
  );
};
